import React, { useState, useContext, useEffect, useReducer, useRef, useMemo } from 'react'
import { MD5 } from 'utils'
import querystring from 'querystring'
import { matchPath, useMatch, useNavigate, useLocation, useParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { reverseLookupMessage } from 'messageUtils'
import { messages } from 'routes'
import useUtmUrl from 'useUtmUrl'
import useApi from 'hooks/useApi'
import { getVoicesUrl, getProjectVoices } from 'data/voices/list'
import { applyTheme } from 'themes/utils'
import { IntlContext } from 'containers/App'
import { useBrand } from 'contexts/BrandContext'
import useLocalStorageState from 'hooks/useLocalStorageState'

export const intents = {
  SHARE: 'SHARE',
  DOWNLOAD: 'DOWNLOAD',
  QUOTE: 'QUOTE',
  SHARE: 'SHARE',
  GENERATE: 'GENERATE',
}

export const initialProject = {
  intent: undefined,
  id: undefined,
  initatedBy: 'customer',
  utm_source: undefined,
  utm_medium: undefined,
  utm_campaign: undefined,
  utm_term: undefined,
  gclid: undefined,
  leadId: undefined,
  dealId: undefined,
  availableFrom: undefined,
  availableTo: undefined,
  brand: {},
  categories: [],
  currentPage: 1,
  genders: [],
  languages: [],
  demoLanguages: [],
  demoDialects: [],
  demoAccents: [],
  languagesProficiency: [],
  mediums: [],
  newSince: undefined,
  serviceIds: [],
  mediaIds: [],
  pageSize: 24,
  order: {
    field: 'priority',
    direction: 'DESC',
  },
  playlist: [],
  query: undefined,
  tags: [],
  voiceages: [18, 100],
  voices: {},
  quotes: [],
  collections: [],
  includeAll: false,
  view: 'GRID', // 'LIST', 'GRID', 'MASONRY'
  details: {
    name: '',
    email: '',
    companyName: '',
    brief: '',
    productType: null,
    productQuantity: null,
    subscribeToNewsletter: false,
  },
  votes: []
}

export const useProject = () => useContext(ProjectContext)

export function ProjectContextProvider({ children }) {
  const getSignature = (object) => {
    const signature = JSON.stringify({
      id: object.id,
      brand: object.brand,
      details: object.details,
      messages: object.messages,
      voices: Object.values(object.voices || {})
      .map(({ sku, character = '', body = '', avatarUrl = '', demos = [] }) => `${sku}-${body}-${character}-${avatarUrl}-${demos.map(({ id }) => id).join('-')}`)
        .sort(),
    })

    return MD5(signature)
  }

  const initialSignature = useRef(null)


  const { formatMessage } = useIntl()
  const location = useLocation()
  const initialProject = getInitialProject(location, formatMessage)
  const [projectState, dispatch] = useReducer(ProjectReducer, initialProject)
  const { intl: { locale = null } = null, dispatch: intlDispatch } = useContext(IntlContext)
  const brand = useBrand()
  const [storedProjectState, storeProjectState] = useLocalStorageState(null, 'voice.portal.project')
  const [restoreAvailable, setRestoreAvailable] = useState(() => {
    if (storedProjectState && getSignature(storedProjectState) !== getSignature(projectState)) {
      return storedProjectState
    }

    return false
  })

  const [data, loading, errors, refetch, meta] = useApi('/rest/v2/project', {
    load: false,
  })

  const fetch = (hashIdOrId) => refetch(`/rest/v2/project/${hashIdOrId}`)

  const create = (storeLocally = false) => {
    if (storeLocally) {
      storeProjectState(projectState)
    }

    refetch({
      method: 'POST',
      body: JSON.stringify({
        ...projectState,
        cid: typeof ga !== 'undefined' ? (ga?.getAll()[0]?.get('clientId') || null) : null,
        locale,
        // Which host created the project. The portal reads it on create only: it decides
        // the CRM division of the lead and the host of the project's links.
        partner: brand.partnerId,
      }),
    })
  }

  const save = (nextProject = null, { then = undefined } = {}) => {
    const project = nextProject || projectState

    if (storedProjectState) {
      storeProjectState(project)
    } else {
      storeProjectState(null)
    }

    refetch(`/rest/v2/project/${projectState.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...project,
        cid: typeof ga !== 'undefined' ? (ga?.getAll()[0]?.get('clientId') || null) : null,
        locale,
      }),
      then,
    })
  }

  const reset = () => {
    if (storedProjectState) {
      storeProjectState(null)
      setRestoreAvailable(false)
    }

    dispatch({ type: 'RESET' })
  }

  const restore = () => {
    if (restoreAvailable) {
      dispatch({ type: 'PATCH', payload: hydrateState(storedProjectState) })
      setRestoreAvailable(false)
    }
  }

  useEffect(() => {
    if (loading) {
      return
    }

    if (data && data.id) {
      dispatch({
        type: 'PATCH',
        payload: data,
      })

      if ((!!data.leadId || !!data.dealId) && !!data.details?.email) {
        storeProjectState(null) // Remove local after a successfull quote request
      }

      if (meta && 'messages' in meta) {
        intlDispatch({ action: 'OVERWRITE', payload: meta.messages })
      }

      if (meta && 'votes' in meta) {
        dispatch({ type: 'SET_VOTES', payload: meta.votes })
      }
    }
  }, [data, meta, loading])

  useEffect(() => {
    if (projectState.id && !projectState.hashId) {
      fetch(projectState.id)
    } else if (Object.keys(projectState.voices).length) {
      getProjectVoices(projectState).then((payload) => {
        dispatch({ type: 'SET_VOICES', payload })
      })
    }
  }, [])

  // A project's own brand wins over the host's, so a project shared from one host keeps
  // its look on the other; without one (or after leaving it) the host's theme returns.
  useEffect(() => {
    applyTheme(projectState?.brand?.theme || brand.theme)
  }, [projectState?.brand?.theme, brand.theme])

  useEffect(() => {
    initialSignature.current = getSignature(projectState)
  }, [
    projectState.id,
    projectState.version,
  ])

  useEffect(() => {
    if (projectState._autoSave) {
      dispatch({ type: 'SET', payload: { key: '_autoSave', value: false }})
      save(projectState)
    }
  }, [projectState])

  const project = {
    ...projectState,
    fetch,
    create,
    save,
    reset,
    restore,
    restoreAvailable,

    get isIdentified() {
      return [
        projectState.details.email,
        projectState.details.name,
        projectState.details.companyName,
      ].every((value) => !!value?.length)
    },

    get signature() {
      return getSignature(this)
    },

    get changed() {
      return initialSignature.current !== this.signature
    },

    get loading() {
      return loading
    },

    get isFetched() {
      return !loading && this.id?.length > 0
    },

    get language() {
      return this.languages[0]
    },

    get demoLanguage() {
      return this.demoLanguages[0]
    },

    get demoDialect() {
      return this.demoDialects[0]
    },

    get demoAccent() {
      return this.demoAccents[0]
    },

    get languageProficiency() {
      return this.languagesProficiency[0]
    },

    get category() {
      return this.categories[0]
    },

    get medium() {
      return this.mediums[0]
    },

    get gender() {
      return this.genders[0]
    },

    get isScoped() {
      return (
        Object.keys(projectState.voices).length > 0
        || projectState.includeAll
        || (
          !!projectState.leadId?.length ||
          !!projectState.dealId?.length
        )
      )
    },

    get isLocallyStored() {
      return !!storedProjectState
    },

    // Which filters may back an auto-updating project. Only filters that the API
    // stores AND that Project::getSuppliers() re-applies belong here — anything
    // else would save a project that silently resolves to a different result set
    // than the one on screen. Query, tone of voice, availability and age are not
    // rebuilt server-side, so they do not qualify on their own.
    get includeAllEligable() {
      return (
        !!project.languages.length ||
        !!project.collections.length ||
        !!project.categories.length ||
        !!project.genders.length ||
        !!project.demoLanguages.length ||
        !!project.demoDialects.length ||
        !!project.demoAccents.length ||
        !!project.newSince
      )
    },

    get voiceStars() {
      return this.collections.findIndex(({ id }) => id == 15) > -1
    },

    get ageTuple() {
      const ages = this.voiceages.map((age) => parseInt(age))

      if (ages.length) {
        return [Math.min(...ages), Math.max(...ages)]
      }

      return [18, 100]
    },

    voices: Object.entries(projectState.voices).reduce((memo, [id, voice]) => {
      memo[id] = {
        ...voice,
        demos: sortDemos(voice.demos, projectState.categories[0])
      }
      return memo
    }, {}),
  }

  return (
    <ProjectContext.Provider value={{ project, dispatch }}>
      {children}
    </ProjectContext.Provider>
  )
}

export const withListener = (WrappedComponent, options = {}) => {
  const HOC = (props) => {
    const location = useLocation()
    const navigate = useNavigate()
    const params = useParams()
    const match = useMatch(location.pathname)
    const { formatMessage } = useIntl()

    const { project, dispatch } = useContext(ProjectContext)

    useEffect(() => {
      if (formatMessage(messages.explore).replace('/*', '/') == match.pathname) {
        const next = useUtmUrl(
          `${location.pathname}?${stateToUrlParams(project)}`,
          location
        )
        const prev = location.pathname + location.search
        if (next !== prev) {
          navigate(next)
        }
      }

      if (params.language && project.languages.length != 1) {
        const next = useUtmUrl(
          formatMessage(messages.explore).replace('/*', '/') + '?' + stateToUrlParams(project),
          location
        )
        const prev = location.pathname + location.search
        if (next !== prev) {
          navigate(next)
        }
      }

      if (
        params.language &&
        params.languageProficiency &&
        params.gender &&
        project.genders.length > 1
      ) {
        const next = useUtmUrl(
          formatMessage(messages.exploreByLanguage, {
            language: match.params.language,
          }),
          location
        )
        const prev = location.pathname + location.search
        if (next !== prev) {
          navigate(next)
        }
      }
    }, [project])

    return <WrappedComponent {...props} />
  }

  return HOC
}

export const ProjectContext = React.createContext({})

export function ProjectReducer(state, action) {
  switch (action.type) {
    case 'ADD_LANGUAGE': {
      return {
        ...state,
        languages: [...new Set([...state.languages, action.payload])],
      }
    }
    case 'ADD_TO_PLAYLIST': {
      return { ...state, playlist: [...state.playlist, action.payload] }
    }
    case 'AVAILABILITY': {
      return {
        ...state,
        availableFrom: action?.payload?.[0] || undefined,
        availableTo: action?.payload?.[1] || undefined,
      }
    }
    case 'CLEAR_CATEGORY': {
      return { ...state, categories: [] }
    }
    case 'CLEAR_MEDIUM': {
      return { ...state, mediums: [] }
    }
    case 'FROM_QUERYSTRING': {
      return urlParamsToState(action.payload, state)
    }
    case 'RESET_FILTERS': {
      let newState = { ...initialProject, voices: { ...state.voices } };
      if (state.id) {
        newState.id = state.id;
      }
      return newState;
    }
    case 'RESET': {
      return initialProject
    }
    case 'SET': {
      return { ...state, [action.payload.key]: action.payload.value }
    }
    case 'SET_AGES': {
      return { ...state, voiceages: action.payload }
    }
    case 'SET_CATEGORY': {
      return {
        ...state,
        categories: [...new Set([...state.categories, action.payload])],
      }
    }
    case 'SET_CATEGORIES': {
      return { ...state, categories: action.payload }
    }
    case 'SET_SERVICE_IDS': {
      return { ...state, serviceIds: action.payload }
    }
    case 'SET_MEDIA_IDS': {
      return { ...state, mediaIds: action.payload }
    }
    case 'SET_GENDERS': {
      return { ...state, genders: action.payload }
    }
    case 'SET_LANGUAGES': {
      return { ...state, languages: action.payload }
    }
    case 'SET_DEMO_LANGUAGES': {
      return { ...state, demoLanguages: action.payload }
    }
    case 'SET_DEMO_DIALECTS': {
      return { ...state, demoDialects: action.payload }
    }
    case 'SET_DEMO_ACCENTS': {
      return { ...state, demoAccents: action.payload }
    }
    case 'SET_LANGUAGES_PROFICIENCY': {
      return { ...state, languagesProficiency: action.payload }
    }
    case 'SET_MEDIUM': {
      return {
        ...state,
        mediums: [...new Set([...state.mediums, action.payload])],
      }
    }
    case 'SET_MEDIUMS': {
      return { ...state, mediums: action.payload }
    }
    case 'SET_NEW_SINCE': {
      return { ...state, newSince: action.payload || undefined }
    }
    case 'SET_TAGS': {
      return { ...state, tags: action.payload }
    }
    case 'ADD_TAG': {
      return { ...state, tags: [...(new Set([...state.tags, action.payload]))] }
    }
    case 'SET_COLLECTIONS': {
      return { ...state, collections: action.payload }
    }
    case 'SET_VOICES': {
      return { ...state, voices: action.payload }
    }
    case 'SET_VOTES': {
      return { ...state, votes: action.payload }
    }
    case 'CHANGE_VIEW': {
      return { ...state, view: action.payload }
    }
    case 'SET_ORDER': {
      return {
        ...state,
        order: { field: action.payload[0], direction: action.payload[1] },
      }
    }
    case 'TOGGLE_VOICESTAR_COLLECTION': {
      return {
        ...state,
        collections: state.collections.find(({ id }) => id == 15)
          ? []
          : [{ name: 'Voice Stars', id: 15 }],
      }
    }
    case 'SET_VOICESTAR_COLLECTION': {
      if (action.payload == false) {
        state.collections = state.collections.filter(({ id }) => id != 15)
      } else if (action.payload == true) {
        state.collections = [
          ...state.collections,
          { name: 'Voice Stars', id: 15 },
        ]
      }

      return { ...state }
    }
    case 'PATCH': {
      const nextState = {
        ...state,
        ...action.payload,
        serviceIds: action.payload.serviceIds ? action.payload.serviceIds.map((id) => Number(id)) : [],
        mediaIds: action.payload.mediaIds ? action.payload.mediaIds.map((id) => Number(id)) : [],
        includeAll: action.payload?.autoIncludeSuppliersFromAttributes || action.payload?.includeAll,
        voices:
          action.payload.suppliers?.reduce(
            (memo, supplier) => ({
              ...memo,
              [supplier.sku]: {
                ...supplier,
                ...(action.payload.voices?.[supplier.sku] ?? {}),
              },
            }),
            {}
          ) ||
          action.payload.voices ||
          state.voices,
      }
      return nextState
    }
    case 'TOGGLE_INCLUDE_ALL': {
      return { ...state, includeAll: !state.includeAll, voices: {} }
    }
    case 'EDIT_BRAND': {
      return {
        ...state,
        brand: { ...state.brand, [action.payload.key]: action.payload.value },
      }
    }

    case 'SEARCH': {
      return { ...state, query: action.payload }
    }

    case 'TOGGLE': {
      if (
        state[action.payload.key].findIndex((v) => v === action.payload.value) >
        -1
      ) {
        return {
          ...state,
          [action.payload.key]: state[action.payload.key].filter(
            (v) => v !== action.payload.value
          ),
        }
      }

      const nextState = {
        ...state,
        [action.payload.key]: [
          ...state[action.payload.key],
          action.payload.value,
        ],
      }

      if (['languages', 'mediums', 'categories'].includes(action.payload.key)) {
        nextState[
          { languages: 'language', mediums: 'medium', categories: 'category' }[
            action.payload.key
          ]
        ] = action.payload.value
      }

      return nextState
    }
    case 'REMOVE_LANGUAGE': {
      return {
        ...state,
        languages: [
          ...new Set(state.languages.filter((key) => key !== action.payload)),
        ],
      }
    }
    case 'REMOVE_DEMO': {
      const voices = state.voices

      voices[action.payload.voice.sku].demos = voices[
        action.payload.voice.sku
      ].demos.filter((demo) => demo.id !== action.payload.demo.id)

      return { ...state, voices }
    }

    case 'ADD_ALL_DEMOS': {
      return {
        ...state,
        voices: {
          ...state.voices,
          [action.payload.voice.sku]: action.payload.voice,
        }
      }
    }

    case 'REMOVE_ALL_DEMOS': {
      return {
        ...state,
        voices: {
          ...state.voices,
          [action.payload.voice.sku]: {
            ...action.payload.voice,
            _demos: action.payload.voice.demos,
            demos: [],
          }
        }
      }
    }

    case 'REMOVE_VOICE': {
      const voices = state.voices
      delete voices[action.payload.sku]
      return { ...state, voices }
    }

    case 'PATCH_VOICE': {
      return {
        ...state,
        voices: {
          ...state.voices,
          [action.payload.sku]: action.payload,
        },
      }
    }

    case 'SUBMIT_EMAIL': {
      return {
        ...state,
        _autoSave: true,
        details: {
          ...state.details,
          ...action.payload
        }
      }
    }

    case 'ADD_VOICE': {
      if (!state.voices[action.payload.voice.sku]) {
        return {
          ...state,
          intent: action.payload?.intent || undefined,
          voices: {
            ...state.voices,
            [action.payload.voice.sku]: {
              ...action.payload.voice,
              _demos: action.payload.voice.demos,
              demos: action.payload.demo
                ? [action.payload.demo]
                : state.categories.length
                ? action.payload.voice.demos.filter((demo) =>
                    state.categories.includes(demo.product)
                  )
                : [],
            },
          },
        }
      }

      if (state.voices[action.payload.voice.sku] && !action.payload.demo) {
        return state
      }

      return {
        ...state,
        intent: action.payload?.intent || undefined,
        voices: {
          ...state.voices,
          [action.payload.voice.sku]: {
            ...state.voices[action.payload.voice.sku],
            _demos: action.payload.voice.demos,
            demos: [
              ...state.voices[action.payload.voice.sku].demos,
              action.payload.demo,
            ],
          },
        },
      }
    }

    default:
      return state
  }
}

const getInitialProject = (location, formatMessage) => {
  const nextState = urlParamsToState(location, initialProject)

  const languageMatch = matchPath(
    formatMessage(messages.exploreByLanguage, { language: ':language' }),
    location.pathname
  )

  const genderMatch = matchPath(
    formatMessage(messages.exploreByLanguageAndGender, {
      language: ':language',
      gender: ':gender',
    }),
    location.pathname
  )

  if (languageMatch) {
    const language = reverseLookupMessage(
      'languages',
      languageMatch.params.language,
      formatMessage
    )
    nextState.language = language
    nextState.languages = [...new Set([...nextState.languages, language])]
  }

  if (genderMatch) {
    const gender = reverseLookupMessage(
      'genders',
      genderMatch.params.gender,
      formatMessage
    )
    const language = reverseLookupMessage(
      'languages',
      genderMatch.params.language,
      formatMessage
    )
    nextState.genders = [...new Set([...nextState.genders, gender])]
    nextState.languages = [...new Set([...nextState.languages, language])]
  }

  return nextState
}

export function stateToUrlParams(rawState) {
  const state = dehydrateState(rawState)
  const params = new URLSearchParams()

  Object.entries(KEYMAP).map(([key, [letter, multi]]) => {
    if (multi && state[key] && state[key].sort) {
      state[key]
        .sort((a, b) => a < b ? -1 : (a > b ? 1 : 0))
        .map((item) => params.append(letter, item))
    } else if (!multi && state[key]) {
      params.set(letter, state[key])
    }
  })

  return params.toString()
}

function urlParamsToState(location, prevState = {}) {
  const params = new URLSearchParams(location.search.substr(1))
  let state = { ...prevState }

  Object.entries(KEYMAP).map(([key, [letter, multi]]) => {
    if (params.has(letter)) {
      state[key] = multi ? params.getAll(letter) : params.get(letter)
    }
  })

  return hydrateState(state)
}

const KEYMAP = {
  categories: ['c', true],
  currentPage: ['p', false],
  genders: ['g', true],
  languages: ['l', true],
  demoLanguages: ['dl', true],
  demoDialects: ['dd', true],
  demoAccents: ['da', true],
  languagesProficiency: ['lp', true],
  mediums: ['m', true],
  newSince: ['n', false],
  pageSize: ['s', false],
  query: ['q', false],
  tags: ['x', true],
  voiceages: ['a', true],
  voices: ['v', true],
  availableTo: ['t', false],
  availableFrom: ['f', false],
  leadId: ['lid', false],
  dealId: ['did', false],
  id: ['pid', false],
  collections: ['col', true],
  utm_campaign: ['utm_campaign', false],
  utm_medium: ['utm_medium', false],
  utm_source: ['utm_source', false],
  utm_term: ['utm_term', false],
  gclid: ['gclid', false],
}

function dehydrateState(rawState) {
  let dehydratedState = {
    ...rawState,
    availableFrom: rawState.availableFrom?.getTime(),
    availableTo: rawState.availableTo?.getTime(),
    collections: rawState.collections.map(({ id }) => id),
    languagesProficiency: rawState.languagesProficiency.map(({ slug }) => slug),
  }

  if (!rawState.dynamic) {
    dehydratedState.voices = Object.values(rawState.voices)
      .sort((a, b) => a.sku < b.sku ? -1 : (a.sku > b.sku ? 1 : 0))
      .map((voice) =>
        !rawState.id && voice.demos.length > 0
        ? voice.sku + ' ' + voice.demos.sort((a, b) => a.id < b.id ? -1 : (a.id > b.id ? 1 : 0)).map((demo) => demo.id).join(' ')
        : voice.sku
      )
  }

  return dehydratedState
}

function hydrateState(rawState) {
  return {
    ...rawState,
    availableTo: rawState.availableTo
      ? new Date(parseInt(rawState.availableTo))
      : rawState.availableTo,
    availableFrom: rawState.availableFrom
      ? new Date(parseInt(rawState.availableFrom))
      : rawState.availableFrom,
    collections: (rawState.collections || []).map((id) => ({
      id: parseInt(id, 10),
      name: '...',
    })),
    languagesProficiency: (rawState.languagesProficiency || []).map((slug) => ({
      slug: slug,
      name: '...',
    })),
    voices: Object.entries(rawState.voices || {}).reduce((memo, [key, voice]) => {
      if (typeof voice == 'string') {
        const [id, ...demoIds] = voice.split(' ')

        memo[id] = {
          id,
          displayName: id,
          sku: id,
          unavailability: [],
          demos: [...new Set(demoIds)].map((id) => ({ id, product: 'unknown' })),
        }
      } else {
        memo[voice.sku] = voice
      }

      return memo
    }, {}),
  }
}

export function sortDemos(demos, productSlug = null) {
  const preferedProductSlugs = [
    'commercial',
    'video-presentation-product-video',
    'elarning'
  ]

  const countByLanguages = demos.reduce((memo, { language }) => {
    memo[language] = (memo[language] || 0) + 1

    return memo
  }, {})

  const topLanguage = Object
    .keys(countByLanguages)
    .sort((a, b) => (
      countByLanguages[a] > countByLanguages[b] ? -1 : 1
    ))?.[0]

  return demos.sort((a, b) => {
    // Sort secondary language demos last
    if (demos.length > 2 && b?.language != topLanguage) {
      return -1
    }

    // Sort custom demo types last
    if (b?.deprecatedTypeName?.length) {
      return b.deprecatedTypeName.length ? 1 : -1
    }

    // Sort demos matching selected product first
    if (productSlug) {
      if (a.productSlug == productSlug && b.productSlug == productSlug) {
        return 0
      }

      if (a.productSlug == productSlug) {
        return -1
      }

      return 1
    }

    // Sort custom demo types last
    if (b?.deprecatedTypeName?.length) {
      return b.deprecatedTypeName.length ? 1 : -1
    }

    // Sort by API specificed sorting, if non of the above conditions match
    if (a.sorting !== b.sorting) {
      return b.sorting > a.sorting ? -1 : 1
    }

    // Fallback to priorise shortlisted products first
    if (preferedProductSlugs.includes(a.productSlug)
      && preferedProductSlugs.includes(b.productSlug)
    ) {
      return 0
    }

    return preferedProductSlugs.includes(a.productSlug) ? -1 : 1;
  })
}
